export interface DecisionNode {
    id: string;
    userCommand: string;
    systemAnswer: string;
    type: string;
    options?: DecisionOption[];
    condition?: (context: Record<string, any>) => boolean;
}

interface DecisionOption {
    label: string;
    nextNodeId: string;
    icon: string;
    scheme: string;
}

const createOption = (labelKey: string, nextNodeId: string, icon: string, scheme: string): DecisionOption => ({
    label: labelKey,
    nextNodeId,
    icon,
    scheme
});

const createIncidentDecisionTree = (): DecisionNode[] => [
    {
        id: "createIncidentNew",
        userCommand: "chatBot.quickActions.actions.createIncident",
        systemAnswer: "chatBot.quickActions.state.whatsTheIssue",
        type: "text",
        condition: (context) => context.category === "new",
    },
    {
        id: "createIncidentOngoing",
        userCommand: "chatBot.quickActions.actions.createIncident",
        systemAnswer: "chatBot.quickActions.state.lastConversation",
        type: "options",
        options: [
            createOption("yes", "ongoingConversation", "radio_checkbox_tick", "neutral800"),
            createOption("no", "newConversation", "cross", "neutral800"),
        ],
        condition: (context) => context.category === "ongoing",
    },
    {
        id: "createIncidentSupportBtn",
        userCommand: "chatBot.quickActions.actions.createIncident",
        systemAnswer: "chatBot.quickActions.state.createIncidentLabel",
        type: "create-incident",
        options: [
            createOption("chatBot.quickActions.state.createTicketLabel", "confirmCreateIncident", "radio_checkbox_tick", "neutral800"),
            createOption("chatBot.quickActions.state.cancelTicketLabel", "cancelCreateIncident", "cross", "red500"),
        ],
        condition: (context) => context.category === "ongoing",
    },
    {
        id: "ongoingConversation",
        userCommand: "chatBot.quickActions.actions.ongoingConversation",
        systemAnswer: "chatBot.quickActions.state.createIncidentLabel",
        type: "create-incident",
        options: [
            createOption("chatBot.quickActions.state.createTicketLabel", "confirmCreateIncident", "radio_checkbox_tick", "neutral800"),
            createOption("chatBot.quickActions.state.cancelTicketLabel", "cancelCreateIncident", "cross", "red500"),
        ],
        condition: (context) => context.category === "ongoing" && context.quickActionType === "createIncident",
    },
    {
        id: "newConversation",
        userCommand: "chatBot.quickActions.actions.newConversation",
        systemAnswer: "chatBot.quickActions.state.whatsTheIssue",
        type: "text",
    },
    {
        id: "confirmCreateIncident",
        userCommand: "chatBot.quickActions.actions.confirmAndCreateTicket",
        systemAnswer: "chatBot.quickActions.state.creatingIncidentTicket",
        type: "create-incident-response",
    },
    {
        id: "cancelCreateIncident",
        userCommand: "chatBot.quickActions.actions.cancelTicketCreation",
        systemAnswer: "chatBot.quickActions.state.cancelAck",
        type: "text",
    },
];

const chatWithServiceDeskDecisionTree = (): DecisionNode[] => [
    {
        id: "chatWithServiceDeskNew",
        userCommand: "chatBot.quickActions.actions.chatWithServiceDesk",
        systemAnswer: "chatBot.quickActions.state.whatsTheIssue",
        type: "text",
        condition: (context) => context.category === "new",
    },
    {
        id: "chatWithServiceDeskOngoing",
        userCommand: "chatBot.quickActions.actions.chatWithServiceDesk",
        systemAnswer: "chatBot.quickActions.state.lastConversation",
        type: "options",
        options: [
            createOption("yes", "ongoingConversationChat", "radio_checkbox_tick", "neutral800"),
            createOption("no", "newConversation", "cross", "neutral800"),
        ],
        condition: (context) => context.category === "ongoing",
    },
    {
        id: "chatWithServiceDeskSupportBtn",
        userCommand: "chatBot.quickActions.actions.chatWithServiceDesk",
        systemAnswer: "chatBot.quickActions.state.chatWithServiceDeskLabel",
        type: "chat-with-service-desk",
        options: [
            createOption("chatBot.quickActions.state.connectWithServiceDeskLabel", "confirmChatWithServiceDesk", "radio_checkbox_tick", "neutral800"),
            createOption("cancel", "cancelChatWithServiceDesk", "cross", "red500"),
        ],
        condition: (context) => context.category === "ongoing",
    },
    {
        id: "ongoingConversationChat",
        userCommand: "chatBot.quickActions.actions.ongoingConversation",
        systemAnswer: "chatBot.quickActions.state.chatWithServiceDeskLabel",
        type: "chat-with-service-desk",
        options: [
            createOption("chatBot.quickActions.state.connectWithServiceDeskLabel", "confirmChatWithServiceDesk", "radio_checkbox_tick", "neutral800"),
            createOption("cancel", "cancelChatWithServiceDesk", "cross", "red500"),
        ],
        condition: (context) => context.category === "ongoing" && context.quickActionType === "chatWithServiceDesk",
    },
    {
        id: "cancelChatWithServiceDesk",
        userCommand: "chatBot.quickActions.actions.cancelChatWithServiceDesk",
        systemAnswer: "chatBot.quickActions.state.cancelAck",
        type: "text",
    },
];

const requestCallbackDecisionTree = (): DecisionNode[] => [
    {
        id: "requestCallbackNew",
        userCommand: "chatBot.quickActions.actions.requestCallback",
        systemAnswer: "chatBot.quickActions.state.whatsTheIssue",
        type: "text",
        condition: (context) => context.category === "new",
    },
    {
        id: "requestCallbackOngoing",
        userCommand: "chatBot.quickActions.actions.requestCallback",
        systemAnswer: "chatBot.quickActions.state.lastConversation",
        type: "options",
        options: [
            createOption("yes", "ongoingConversationRequestCallback", "radio_checkbox_tick", "neutral800"),
            createOption("no", "newConversation", "cross", "neutral800"),
        ],
        condition: (context) => context.category === "ongoing",
    },
    {
        id: "requestCallbackSupportBtn",
        userCommand: "chatBot.quickActions.actions.requestCallback",
        systemAnswer: "chatBot.quickActions.state.requestCallbackPromptAnswer",
        type: "request-callback-prompt",
        options: [
            createOption("chatBot.quickActions.state.requestCallbackConfirmLabel", "ongoingConversationRequestCallbackForm", "radio_checkbox_tick", "neutral800"),
            createOption("cancel", "cancelRequestCallback", "cross", "red500"),
        ],
        condition: (context) => context.category === "ongoing",
    },
    {
        id: "ongoingConversationRequestCallback",
        userCommand: "chatBot.quickActions.actions.ongoingConversation",
        systemAnswer: "chatBot.quickActions.state.requestCallbackPromptAnswer",
        type: "request-callback-prompt",
        options: [
            createOption("chatBot.quickActions.state.requestCallbackConfirmLabel", "ongoingConversationRequestCallbackForm", "radio_checkbox_tick", "neutral800"),
            createOption("cancel", "cancelRequestCallback", "cross", "red500"),
        ],
        condition: (context) => context.category === "ongoing" && context.quickActionType === "requestCallback",
    },
    {
        id: "ongoingConversationRequestCallbackForm",
        userCommand: "chatBot.quickActions.state.requestCallbackConfirmLabel",
        systemAnswer: "chatBot.quickActions.state.requestCallbackLabel",
        type: "request-callback",
        options: [
            createOption("chatBot.quickActions.state.requestCallbackLabel", "confirmRequestCallback", "radio_checkbox_tick", "neutral800"),
            createOption("cancel", "cancelRequestCallback", "cross", "red500"),
        ],
    },
    {
        id: "confirmRequestCallback",
        userCommand: "chatBot.quickActions.actions.confirmRequestCallback",
        systemAnswer: "",
        type: "request-callback-result",
        options: [
            createOption("chatBot.requestCallback.tryAgain", "ongoingConversationRequestCallback", "", "neutral800"),
            createOption("cancel", "cancelRequestCallback", "", "red500"),
        ],
    },
    {
        id: "cancelRequestCallback",
        userCommand: "chatBot.quickActions.actions.cancelRequestCallback",
        systemAnswer: "chatBot.quickActions.state.cancelAck",
        type: "text",
    },
];

export const getDecisionTree = (): DecisionNode[] => [
    ...createIncidentDecisionTree(),
    ...chatWithServiceDeskDecisionTree(),
    ...requestCallbackDecisionTree()
];

export const getUserCommand = (nodeId: string): string =>
    getDecisionTree().find(item => item.id === nodeId)?.userCommand ?? "";

export const getDecisionNode = (nodeId: string): DecisionNode | null =>
    getDecisionTree().find(item => item.id === nodeId) ?? null;